import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import axios from '../../library/axios';
import { logo } from '../../assets';
import {
  CPANEL_LOGIN_PATH,
  isCpanelAuthenticated,
  logoutCpanel,
} from '../../helpers/cpanelAuth';
import { useCpanelDocumentTitle } from '../../helpers/useCpanelDocumentTitle';
import CpanelNavbar from './CpanelNavbar';
import CpanelHeader from './CpanelHeader';
import CpanelToastContainer from './CpanelToastContainer';
import CpanelNotificationRealtime from './CpanelNotificationRealtime';
import './cpanel.css';
import './cpanel-buttons.css';

const CPANEL_PAGE_TITLES = {
  '/cpanel/dashboard': 'Dashboard',
  '/cpanel/tenants': 'Tenant',
  '/cpanel/users': 'User',
  '/cpanel/logs': 'Log',
  '/cpanel/profile': 'Profil',
  '/cpanel/notifications': 'Notifikasi',
  '/cpanel/jobs': 'Job Runner',
};

const CpanelLayout = () => {
  const location = useLocation();
  const pageTitle = useMemo(() => {
    if (location.pathname.startsWith('/cpanel/jobs/')) {
      return 'Job Runner';
    }
    if (location.pathname.startsWith('/cpanel/notifications')) {
      return 'Notifikasi';
    }
    return CPANEL_PAGE_TITLES[location.pathname] || '';
  }, [location.pathname]);

  useCpanelDocumentTitle(pageTitle);
  const navigate = useNavigate();
  const authenticated = isCpanelAuthenticated();

  useEffect(() => {
    if (!authenticated) {
      navigate(CPANEL_LOGIN_PATH, { replace: true });
    }
  }, [authenticated, navigate]);

  useEffect(() => {
    document.body.classList.add('cpanel-chrome');
    return () => document.body.classList.remove('cpanel-chrome');
  }, []);

  useEffect(() => {
    const syncMetronic = () => {
      if (window.matchMedia('(max-width: 991.98px)').matches) {
        document.body.removeAttribute('data-kt-aside-minimize');
      }
      if (typeof window.KTMenu !== 'undefined') {
        window.KTMenu.createInstances();
      }
      if (typeof window.KTToggle !== 'undefined') {
        window.KTToggle.createInstances();
      }
      if (typeof window.KTScroll !== 'undefined') {
        window.KTScroll.createInstances();
      }
      if (typeof window.KTDrawer !== 'undefined') {
        window.KTDrawer.createInstances();
      }
    };

    syncMetronic();
    window.addEventListener('resize', syncMetronic);
    return () => window.removeEventListener('resize', syncMetronic);
  }, [location.pathname]);

  if (!authenticated) {
    return null;
  }

  const handleLogout = () => {
    logoutCpanel({ navigate, axiosClient: axios });
  };

  return (
    <div className="d-flex flex-column flex-root">
      <CpanelNotificationRealtime />
      <CpanelToastContainer />
      <div className="page d-flex flex-row flex-column-fluid">
        <CpanelNavbar />
        <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
          <div id="kt_header" className="header align-items-stretch cpanel-topbar">
            <div className="container-fluid d-flex align-items-stretch justify-content-between">
              <div className="d-flex align-items-center d-lg-none ms-n3 me-1" title="Show aside menu">
                <div
                  className="btn btn-icon btn-active-light-primary w-30px h-30px w-md-40px h-md-40px"
                  id="kt_aside_mobile_toggle"
                >
                  <span className="svg-icon svg-icon-2x mt-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width={24} height={24} viewBox="0 0 24 24" fill="none">
                      <path d="M21 7H3C2.4 7 2 6.6 2 6V4C2 3.4 2.4 3 3 3H21C21.6 3 22 3.4 22 4V6C22 6.6 21.6 7 21 7Z" fill="black" />
                      <path opacity="0.3" d="M21 14H3C2.4 14 2 13.6 2 13V11C2 10.4 2.4 10 3 10H21C21.6 10 22 10.4 22 11V13C22 13.6 21.6 14 21 14ZM22 20V18C22 17.4 21.6 17 21 17H3C2.4 17 2 17.4 2 18V20C2 20.6 2.4 21 3 21H21C21.6 21 22 20.6 22 20Z" fill="black" />
                    </svg>
                  </span>
                </div>
              </div>
              <div className="d-flex align-items-center flex-grow-1 flex-lg-grow-0 d-lg-none">
                <a href="/cpanel/dashboard">
                  <img alt="Logo" src={logo} className="h-30px" />
                </a>
              </div>
              <CpanelHeader onLogout={handleLogout} />
            </div>
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default CpanelLayout;
